import {
  CODE_GRADE_CLAMP,
  type Edge,
  type Route,
  type Segment,
  type SegmentType,
  type Vec3,
  type WorldRef,
} from "@cimara/sim";

/** Binary format version understood by this loader. */
export const WORLD_FORMAT_VERSION = 0;
/** Magic bytes at the start of every world file. */
export const WORLD_MAGIC = "CIMW";
/** Authored grades are clamped to this percent value by the runtime. */
export const MAX_ROAD_GRADE_PERCENT = CODE_GRADE_CLAMP * 100;

const HEADER_BYTES = 4 + 2 + 4 * 4;
const NODE_BYTES = 12;
const EDGE_HEADER_BYTES = 16;
const ROUTE_HEADER_BYTES = 6;
// The spec's field list sums to 22 bytes, but it fixes the segment stride at
// 24 bytes. The writer uses natural 4-byte alignment: a 2-byte pad after the
// u16 id, then the fields, then the 3 reserved bytes.
const SEGMENT_BYTES = 24;

// Sanity limits. They are far above the Phase 0 world (a few thousand points)
// and keep a corrupt count from turning into a huge allocation or loop.
const MAX_NODE_COUNT = 1_000_000;
const MAX_EDGE_COUNT = 200_000;
const MAX_ROUTE_COUNT = 10_000;
const MAX_SEGMENT_COUNT = 100_000;
const MAX_POINTS_PER_EDGE = 5_000_000;

const SEGMENT_TYPES: readonly SegmentType[] = ["sprint", "kom", "cobble"];
const SEGMENT_EPSILON_M = 0.5;

export interface WorldSpawn {
  edge: number;
  s: number;
  lane: number;
}

export interface WorldBounds {
  min: Vec3;
  max: Vec3;
}

export interface WorldManifest {
  version: string;
  seed: number;
  sha256: string;
  route: Route;
  routes: Route[];
  segments: Segment[];
  spawn: WorldSpawn;
  bounds: WorldBounds;
}

export interface World extends WorldRef {
  version: string;
  seed: number;
  sha256: string;
  routes: Route[];
  segments: Segment[];
  spawn: WorldSpawn;
  bounds: WorldBounds;
  getRoute(id: number): Route | undefined;
  getSegment(id: number): Segment | undefined;
  getLength(): number;
}

export interface RoadGeometry {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
}

/** Thrown when the binary world or its manifest violates the frozen format. */
export class WorldFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WorldFormatError";
  }
}

interface EdgeProfile {
  /** Cumulative 3D distance from the first point, in meters. */
  distances: Float64Array;
  /** Cumulative distance in the x/z plane, used for rise-over-run grade. */
  planDistances: Float64Array;
}

interface BinaryRoute {
  id: number;
  edgeIds: number[];
  distanceM: number;
  climbM: number;
}

interface BinarySegment {
  id: number;
  edgeStart: number;
  edgeEnd: number;
  sStart: number;
  sEnd: number;
  type: SegmentType;
}

/**
 * Parse the compact binary world produced by worldgen and verify it against
 * the manifest (version and SHA-256).
 *
 * The manifest supplies names and metadata; the binary is authoritative for
 * geometry. A manifest route whose edge list disagrees with the binary is a
 * contract violation and is rejected.
 */
export async function parseWorld(buffer: ArrayBuffer, manifest: WorldManifest): Promise<World> {
  if (!(buffer instanceof ArrayBuffer)) {
    throw new WorldFormatError("world data must be an ArrayBuffer");
  }
  if (!manifest || typeof manifest !== "object") {
    throw new WorldFormatError("world manifest is missing");
  }

  const reader = new BinaryReader(buffer, 0);

  if (reader.remaining < HEADER_BYTES) {
    throw new WorldFormatError(
      `truncated header: ${reader.remaining} bytes, expected at least ${HEADER_BYTES}`,
    );
  }

  const magic = String.fromCharCode(
    reader.u8("magic 0"),
    reader.u8("magic 1"),
    reader.u8("magic 2"),
    reader.u8("magic 3"),
  );
  if (magic !== WORLD_MAGIC) {
    throw new WorldFormatError(`bad magic ${JSON.stringify(magic)}, expected ${JSON.stringify(WORLD_MAGIC)}`);
  }

  const binaryVersion = reader.u16("format version");
  if (binaryVersion !== WORLD_FORMAT_VERSION) {
    throw new WorldFormatError(
      `unsupported world version ${binaryVersion}, expected ${WORLD_FORMAT_VERSION}`,
    );
  }

  const manifestVersion = parseManifestMajor(manifest.version);
  if (manifestVersion === null) {
    throw new WorldFormatError(`manifest version ${JSON.stringify(manifest.version)} is not a version string`);
  }
  if (manifestVersion !== binaryVersion) {
    throw new WorldFormatError(
      `manifest version ${JSON.stringify(manifest.version)} does not match binary world version ${binaryVersion}`,
    );
  }
  if (!Number.isFinite(manifest.seed)) {
    throw new WorldFormatError("manifest seed must be a finite number");
  }

  const nodeCount = reader.u32("nodeCount");
  const edgeCount = reader.u32("edgeCount");
  const routeCount = reader.u32("routeCount");
  const segmentCount = reader.u32("segmentCount");

  validateCounts(reader, nodeCount, edgeCount, routeCount, segmentCount);

  if (typeof manifest.sha256 !== "string" || !/^[0-9a-f]{64}$/i.test(manifest.sha256)) {
    throw new WorldFormatError("manifest sha256 must be a 64-character hexadecimal string");
  }
  const expectedHash = manifest.sha256.toLowerCase();
  const actualHash = await sha256Hex(buffer);
  if (actualHash !== expectedHash) {
    throw new WorldFormatError(
      `SHA-256 mismatch: manifest has ${expectedHash}, file has ${actualHash}`,
    );
  }

  const nodes = readNodes(reader, nodeCount);
  const { edges, profiles, outgoing } = readEdges(reader, nodeCount, edgeCount);
  const binaryRoutes = readRoutes(reader, edges, routeCount);
  const binarySegments = readSegments(reader, edges, segmentCount);

  if (reader.remaining !== 0) {
    throw new WorldFormatError(
      `unexpected ${reader.remaining} trailing bytes at byte ${reader.offset}`,
    );
  }

  const routes = mergeRoutes(binaryRoutes, manifest);
  const segments = mergeSegments(binarySegments, manifest);
  const bounds = readBounds(manifest);
  const spawn = readSpawn(manifest, edges);

  const routeById = new Map(routes.map((route) => [route.id, route]));
  const segmentById = new Map(segments.map((segment) => [segment.id, segment]));
  const primaryRoute = typeof manifest.route?.id === "number"
    ? routeById.get(manifest.route.id)
    : routes[0];
  const totalEdgeLength = edges.reduce((sum, edge) => sum + edge.length, 0);
  const primaryLength = primaryRoute?.distanceM ?? totalEdgeLength;

  const getEdge = (id: number): Edge | undefined => {
    if (!Number.isInteger(id) || id < 0 || id >= edges.length) return undefined;
    return edges[id];
  };

  const getProfile = (id: number): EdgeProfile | undefined => {
    if (!Number.isInteger(id) || id < 0 || id >= profiles.length) return undefined;
    return profiles[id];
  };

  // Keep `nodes` referenced for validation/extension even though the public
  // query API is edge based.
  void nodes;

  return {
    version: manifest.version,
    seed: manifest.seed,
    sha256: actualHash,
    routes,
    segments,
    spawn,
    bounds,
    getEdge,
    edgeLength(id: number): number {
      return getEdge(id)?.length ?? 0;
    },
    sampleGrade(edgeId: number, s: number): number {
      const edge = getEdge(edgeId);
      const profile = getProfile(edgeId);
      if (!edge || !profile || Number.isNaN(s)) return 0;

      const clampedS = clamp(s, 0, edge.length);
      const index = findProfileIndex(profile.distances, clampedS);
      const run = profile.planDistances[index + 1] - profile.planDistances[index];
      if (!(run > 1e-9)) return 0;

      const points = edge.points;
      const rise = points[(index + 1) * 3 + 1] - points[index * 3 + 1];
      const grade = (rise / run) * 100;
      if (!Number.isFinite(grade)) return 0;
      return clamp(grade, -MAX_ROAD_GRADE_PERCENT, MAX_ROAD_GRADE_PERCENT);
    },
    nextEdge(edgeId: number, _s: number): number | null {
      // Phase 0 has exactly one outgoing edge per node. The `s` argument is
      // part of the frozen WorldRef contract and is reserved for future forks.
      const edge = getEdge(edgeId);
      if (!edge) return null;
      const following = outgoing.get(edge.to);
      return following !== undefined && following.length > 0 ? following[0] : null;
    },
    samplePosition(edgeId: number, s: number): Vec3 {
      const edge = getEdge(edgeId);
      const profile = getProfile(edgeId);
      if (!edge || !profile || Number.isNaN(s)) return { x: 0, y: 0, z: 0 };

      const points = edge.points;
      if (s <= 0) {
        return { x: points[0], y: points[1], z: points[2] };
      }
      if (s >= edge.length) {
        const last = points.length - 3;
        return { x: points[last], y: points[last + 1], z: points[last + 2] };
      }

      const index = findProfileIndex(profile.distances, s);
      const span = profile.distances[index + 1] - profile.distances[index];
      const t = span > 1e-9 ? (s - profile.distances[index]) / span : 0;
      const a = index * 3;
      const b = (index + 1) * 3;
      return {
        x: points[a] + (points[b] - points[a]) * t,
        y: points[a + 1] + (points[b + 1] - points[a + 1]) * t,
        z: points[a + 2] + (points[b + 2] - points[a + 2]) * t,
      };
    },
    getRoute(id: number): Route | undefined {
      return routeById.get(id);
    },
    getSegment(id: number): Segment | undefined {
      return segmentById.get(id);
    },
    getLength(): number {
      return primaryLength;
    },
  };
}

class BinaryReader {
  private readonly view: DataView;
  private cursor: number;

  constructor(buffer: ArrayBuffer, start: number) {
    this.view = new DataView(buffer);
    this.cursor = start;
  }

  get offset(): number {
    return this.cursor;
  }

  get remaining(): number {
    return this.view.byteLength - this.cursor;
  }

  ensure(bytes: number, label: string): void {
    if (bytes < 0 || this.cursor + bytes > this.view.byteLength) {
      throw new WorldFormatError(
        `truncated ${label} at byte ${this.cursor}: need ${bytes} bytes, have ${this.remaining}`,
      );
    }
  }

  u8(label: string): number {
    this.ensure(1, label);
    const value = this.view.getUint8(this.cursor);
    this.cursor += 1;
    return value;
  }

  u16(label: string): number {
    this.ensure(2, label);
    const value = this.view.getUint16(this.cursor, true);
    this.cursor += 2;
    return value;
  }

  u32(label: string): number {
    this.ensure(4, label);
    const value = this.view.getUint32(this.cursor, true);
    this.cursor += 4;
    return value;
  }

  f32(label: string): number {
    this.ensure(4, label);
    const value = this.view.getFloat32(this.cursor, true);
    this.cursor += 4;
    return value;
  }

  skip(bytes: number, label: string): void {
    this.ensure(bytes, label);
    this.cursor += bytes;
  }
}

function validateCounts(
  reader: BinaryReader,
  nodeCount: number,
  edgeCount: number,
  routeCount: number,
  segmentCount: number,
): void {
  if (nodeCount > MAX_NODE_COUNT) {
    throw new WorldFormatError(`nodeCount ${nodeCount} exceeds the limit of ${MAX_NODE_COUNT}`);
  }
  if (edgeCount > MAX_EDGE_COUNT) {
    throw new WorldFormatError(`edgeCount ${edgeCount} exceeds the limit of ${MAX_EDGE_COUNT}`);
  }
  if (routeCount > MAX_ROUTE_COUNT) {
    throw new WorldFormatError(`routeCount ${routeCount} exceeds the limit of ${MAX_ROUTE_COUNT}`);
  }
  if (segmentCount > MAX_SEGMENT_COUNT) {
    throw new WorldFormatError(`segmentCount ${segmentCount} exceeds the limit of ${MAX_SEGMENT_COUNT}`);
  }

  const minimumBytes =
    nodeCount * NODE_BYTES +
    edgeCount * EDGE_HEADER_BYTES +
    routeCount * ROUTE_HEADER_BYTES +
    segmentCount * SEGMENT_BYTES;
  if (minimumBytes > reader.remaining) {
    throw new WorldFormatError(
      `declared counts need at least ${minimumBytes} bytes, but only ${reader.remaining} remain`,
    );
  }
}

function readNodes(reader: BinaryReader, nodeCount: number): Float32Array {
  reader.ensure(nodeCount * NODE_BYTES, "nodes");
  const nodes = new Float32Array(nodeCount * 3);
  for (let i = 0; i < nodeCount; i += 1) {
    nodes[i * 3] = readFinite(reader, `node ${i} x`);
    nodes[i * 3 + 1] = readFinite(reader, `node ${i} y`);
    nodes[i * 3 + 2] = readFinite(reader, `node ${i} z`);
  }
  return nodes;
}

function readEdges(
  reader: BinaryReader,
  nodeCount: number,
  edgeCount: number,
): { edges: Edge[]; profiles: EdgeProfile[]; outgoing: Map<number, number[]> } {
  const edges: Edge[] = new Array(edgeCount);
  const profiles: EdgeProfile[] = new Array(edgeCount);
  const outgoing = new Map<number, number[]>();

  for (let id = 0; id < edgeCount; id += 1) {
    const from = reader.u32(`edge ${id} fromNode`);
    const to = reader.u32(`edge ${id} toNode`);
    const pointCount = reader.u32(`edge ${id} pointCount`);
    const width = reader.f32(`edge ${id} width`);

    if (from >= nodeCount) {
      throw new WorldFormatError(`edge ${id} fromNode ${from} is out of range (${nodeCount} nodes)`);
    }
    if (to >= nodeCount) {
      throw new WorldFormatError(`edge ${id} toNode ${to} is out of range (${nodeCount} nodes)`);
    }
    if (pointCount < 2) {
      throw new WorldFormatError(`edge ${id} has pointCount ${pointCount}, expected at least 2`);
    }
    if (pointCount > MAX_POINTS_PER_EDGE) {
      throw new WorldFormatError(
        `edge ${id} pointCount ${pointCount} exceeds the limit of ${MAX_POINTS_PER_EDGE}`,
      );
    }
    if (!Number.isFinite(width) || width <= 0) {
      throw new WorldFormatError(`edge ${id} has invalid width ${width}`);
    }

    reader.ensure(pointCount * 12, `edge ${id} points`);
    const points = new Float32Array(pointCount * 3);
    for (let p = 0; p < pointCount; p += 1) {
      points[p * 3] = readFinite(reader, `edge ${id} point ${p} x`);
      points[p * 3 + 1] = readFinite(reader, `edge ${id} point ${p} y`);
      points[p * 3 + 2] = readFinite(reader, `edge ${id} point ${p} z`);
    }

    const profile = buildProfile(points, id);
    const edge: Edge = { id, from, to, length: profile.distances[pointCount - 1], width, points };
    edges[id] = edge;
    profiles[id] = profile;

    const list = outgoing.get(from);
    if (list === undefined) {
      outgoing.set(from, [id]);
    } else {
      list.push(id);
    }
  }

  return { edges, profiles, outgoing };
}

function buildProfile(points: Float32Array, edgeId: number): EdgeProfile {
  const pointCount = points.length / 3;
  const distances = new Float64Array(pointCount);
  const planDistances = new Float64Array(pointCount);

  for (let i = 1; i < pointCount; i += 1) {
    const a = (i - 1) * 3;
    const b = i * 3;
    const dx = points[b] - points[a];
    const dy = points[b + 1] - points[a + 1];
    const dz = points[b + 2] - points[a + 2];
    distances[i] = distances[i - 1] + Math.hypot(dx, dy, dz);
    planDistances[i] = planDistances[i - 1] + Math.hypot(dx, dz);
  }

  if (!(distances[pointCount - 1] > 0) || !Number.isFinite(distances[pointCount - 1])) {
    throw new WorldFormatError(`edge ${edgeId} has zero or non-finite length`);
  }

  return { distances, planDistances };
}

function readRoutes(reader: BinaryReader, edges: Edge[], routeCount: number): BinaryRoute[] {
  const routes: BinaryRoute[] = new Array(routeCount);
  const seen = new Set<number>();

  for (let i = 0; i < routeCount; i += 1) {
    const id = reader.u16(`route ${i} id`);
    const edgeCount = reader.u32(`route ${id} edgeCount`);

    if (seen.has(id)) {
      throw new WorldFormatError(`duplicate route id ${id}`);
    }
    seen.add(id);
    if (edgeCount === 0) {
      throw new WorldFormatError(`route ${id} has no edges`);
    }

    reader.ensure(edgeCount * 4, `route ${id} edgeIds`);
    const edgeIds = new Array<number>(edgeCount);
    for (let e = 0; e < edgeCount; e += 1) {
      const edgeId = reader.u32(`route ${id} edgeId ${e}`);
      if (edgeId >= edges.length) {
        throw new WorldFormatError(
          `route ${id} references edge ${edgeId}, but only ${edges.length} edges exist`,
        );
      }
      edgeIds[e] = edgeId;
    }

    for (let e = 0; e < edgeCount; e += 1) {
      const current = edges[edgeIds[e]];
      const next = edges[edgeIds[(e + 1) % edgeCount]];
      if (current.to !== next.from) {
        throw new WorldFormatError(
          `route ${id} is not connected: edge ${current.id} ends at node ${current.to}, ` +
            `edge ${next.id} starts at node ${next.from}`,
        );
      }
    }

    let distanceM = 0;
    let climbM = 0;
    for (const edgeId of edgeIds) {
      const edge = edges[edgeId];
      distanceM += edge.length;
      climbM += positiveAscent(edge.points);
    }

    routes[i] = { id, edgeIds, distanceM, climbM };
  }

  return routes;
}

function readSegments(
  reader: BinaryReader,
  edges: Edge[],
  segmentCount: number,
): BinarySegment[] {
  const segments: BinarySegment[] = new Array(segmentCount);
  const seen = new Set<number>();

  for (let i = 0; i < segmentCount; i += 1) {
    const id = reader.u16(`segment ${i} id`);
    reader.skip(2, `segment ${id} alignment`);
    const edgeStart = reader.u32(`segment ${id} edgeStart`);
    const edgeEnd = reader.u32(`segment ${id} edgeEnd`);
    const sStart = reader.f32(`segment ${id} sStart`);
    const sEnd = reader.f32(`segment ${id} sEnd`);
    const typeCode = reader.u8(`segment ${id} type`);
    reader.skip(3, `segment ${id} reserved`);

    if (seen.has(id)) {
      throw new WorldFormatError(`duplicate segment id ${id}`);
    }
    seen.add(id);

    if (edgeStart >= edges.length) {
      throw new WorldFormatError(
        `segment ${id} references edge ${edgeStart}, but only ${edges.length} edges exist`,
      );
    }
    if (edgeEnd >= edges.length) {
      throw new WorldFormatError(
        `segment ${id} references edge ${edgeEnd}, but only ${edges.length} edges exist`,
      );
    }
    if (edgeStart > edgeEnd) {
      throw new WorldFormatError(`segment ${id} edgeStart ${edgeStart} is after edgeEnd ${edgeEnd}`);
    }
    if (!Number.isFinite(sStart) || !Number.isFinite(sEnd)) {
      throw new WorldFormatError(`segment ${id} has non-finite bounds`);
    }
    if (sStart < -SEGMENT_EPSILON_M || sStart > edges[edgeStart].length + SEGMENT_EPSILON_M) {
      throw new WorldFormatError(
        `segment ${id} sStart ${sStart} is outside edge ${edgeStart} (${edges[edgeStart].length} m)`,
      );
    }
    if (sEnd < -SEGMENT_EPSILON_M || sEnd > edges[edgeEnd].length + SEGMENT_EPSILON_M) {
      throw new WorldFormatError(
        `segment ${id} sEnd ${sEnd} is outside edge ${edgeEnd} (${edges[edgeEnd].length} m)`,
      );
    }
    if (edgeStart === edgeEnd && sEnd < sStart - SEGMENT_EPSILON_M) {
      throw new WorldFormatError(`segment ${id} ends before it starts`);
    }
    if (typeCode >= SEGMENT_TYPES.length) {
      throw new WorldFormatError(`segment ${id} has unknown type code ${typeCode}`);
    }

    segments[i] = {
      id,
      edgeStart,
      edgeEnd,
      sStart,
      sEnd,
      type: SEGMENT_TYPES[typeCode],
    };
  }

  return segments;
}

function mergeRoutes(binaryRoutes: BinaryRoute[], manifest: WorldManifest): Route[] {
  const metadata = indexManifestRoutes(manifest);
  return binaryRoutes.map((route) => {
    const meta = metadata.get(route.id);
    if (meta?.edgeIds !== undefined) {
      if (!Array.isArray(meta.edgeIds)) {
        throw new WorldFormatError(`manifest route ${route.id} edgeIds must be an array`);
      }
      if (!sameNumbers(meta.edgeIds, route.edgeIds)) {
        throw new WorldFormatError(
          `manifest route ${route.id} edgeIds do not match the binary route`,
        );
      }
    }

    return {
      id: route.id,
      name: typeof meta?.name === "string" && meta.name.length > 0 ? meta.name : `Route ${route.id}`,
      edgeIds: route.edgeIds,
      distanceM: route.distanceM,
      climbM: route.climbM,
    };
  });
}

function mergeSegments(binarySegments: BinarySegment[], manifest: WorldManifest): Segment[] {
  const metadata = indexManifestSegments(manifest);
  return binarySegments.map((segment) => {
    const meta = metadata.get(segment.id);
    if (meta?.type !== undefined) {
      const declaredType = String(meta.type).toLowerCase();
      if (!isSegmentType(declaredType)) {
        throw new WorldFormatError(`manifest segment ${segment.id} has unknown type ${meta.type}`);
      }
      if (declaredType !== segment.type) {
        throw new WorldFormatError(
          `manifest segment ${segment.id} type ${declaredType} does not match binary type ${segment.type}`,
        );
      }
    }

    return {
      id: segment.id,
      name: typeof meta?.name === "string" && meta.name.length > 0
        ? meta.name
        : `Segment ${segment.id}`,
      edgeStart: segment.edgeStart,
      edgeEnd: segment.edgeEnd,
      sStart: segment.sStart,
      sEnd: segment.sEnd,
      type: segment.type,
    };
  });
}

function indexManifestRoutes(manifest: WorldManifest): Map<number, Route> {
  const result = new Map<number, Route>();
  const routes = Array.isArray(manifest.routes) && manifest.routes.length > 0
    ? manifest.routes
    : manifest.route
      ? [manifest.route]
      : [];

  for (const route of routes) {
    if (!route || typeof route.id !== "number" || !Number.isInteger(route.id)) {
      throw new WorldFormatError("manifest route is missing a valid id");
    }
    if (result.has(route.id)) {
      throw new WorldFormatError(`manifest has duplicate route id ${route.id}`);
    }
    result.set(route.id, route);
  }
  return result;
}

function indexManifestSegments(manifest: WorldManifest): Map<number, Segment> {
  const result = new Map<number, Segment>();
  const segments = Array.isArray(manifest.segments) ? manifest.segments : [];

  for (const segment of segments) {
    if (!segment || typeof segment.id !== "number" || !Number.isInteger(segment.id)) {
      throw new WorldFormatError("manifest segment is missing a valid id");
    }
    if (result.has(segment.id)) {
      throw new WorldFormatError(`manifest has duplicate segment id ${segment.id}`);
    }
    result.set(segment.id, segment);
  }
  return result;
}

function readSpawn(manifest: WorldManifest, edges: Edge[]): WorldSpawn {
  const spawn = manifest.spawn;
  if (!spawn || typeof spawn !== "object") {
    throw new WorldFormatError("manifest spawn is missing");
  }
  if (!Number.isInteger(spawn.edge) || spawn.edge < 0 || spawn.edge >= edges.length) {
    throw new WorldFormatError(`manifest spawn edge ${spawn.edge} is invalid`);
  }
  if (!Number.isFinite(spawn.s) || spawn.s < -SEGMENT_EPSILON_M || spawn.s > edges[spawn.edge].length + SEGMENT_EPSILON_M) {
    throw new WorldFormatError(
      `manifest spawn s ${spawn.s} is outside edge ${spawn.edge} (${edges[spawn.edge].length} m)`,
    );
  }
  if (!Number.isFinite(spawn.lane)) {
    throw new WorldFormatError(`manifest spawn lane ${spawn.lane} is invalid`);
  }

  return { edge: spawn.edge, s: spawn.s, lane: spawn.lane };
}

function readBounds(manifest: WorldManifest): WorldBounds {
  const bounds = manifest.bounds;
  if (!bounds || !isVec3(bounds.min) || !isVec3(bounds.max)) {
    throw new WorldFormatError("manifest bounds must have finite min and max positions");
  }
  if (bounds.min.x > bounds.max.x || bounds.min.y > bounds.max.y || bounds.min.z > bounds.max.z) {
    throw new WorldFormatError("manifest bounds min must not exceed max");
  }
  return {
    min: { x: bounds.min.x, y: bounds.min.y, z: bounds.min.z },
    max: { x: bounds.max.x, y: bounds.max.y, z: bounds.max.z },
  };
}

/**
 * Build a triangle ribbon for a route (or the whole graph) for Three.js.
 *
 * Coordinates are Y-up. Vertices are laid out left/right per centreline point;
 * triangles wind counter-clockwise when viewed from above. The helper uses
 * plain typed arrays and does not import Three.js.
 */
export function buildRoadGeometry(world: World, routeId?: number): RoadGeometry {
  const edges = routeId === undefined ? collectGraphEdges(world) : collectRouteEdges(world, routeId);

  let vertexCount = 0;
  let indexCount = 0;
  for (const edge of edges) {
    const pointCount = validateEdgeForGeometry(edge);
    vertexCount += pointCount * 2;
    indexCount += (pointCount - 1) * 6;
  }

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const indices = new Uint32Array(indexCount);
  let vertexWrite = 0;
  let indexWrite = 0;

  for (const edge of edges) {
    const points = edge.points;
    const pointCount = points.length / 3;
    const halfWidth = edge.width / 2;
    const vertexStart = vertexWrite / 3;
    let lastLateralX = 1;
    let lastLateralZ = 0;

    for (let i = 0; i < pointCount; i += 1) {
      const previous = i > 0 ? i - 1 : i;
      const following = i < pointCount - 1 ? i + 1 : i;
      const a = previous * 3;
      const b = following * 3;

      let tx = points[b] - points[a];
      let ty = points[b + 1] - points[a + 1];
      let tz = points[b + 2] - points[a + 2];
      let tangentLength = Math.hypot(tx, ty, tz);
      if (tangentLength < 1e-9) {
        tx = 0;
        ty = 0;
        tz = 1;
        tangentLength = 1;
      }
      tx /= tangentLength;
      ty /= tangentLength;
      tz /= tangentLength;

      const horizontal = Math.hypot(tx, tz);
      let lateralX = lastLateralX;
      let lateralZ = lastLateralZ;
      if (horizontal > 1e-9) {
        lateralX = -tz / horizontal;
        lateralZ = tx / horizontal;
        lastLateralX = lateralX;
        lastLateralZ = lateralZ;
      }

      let normalX = -lateralZ * ty;
      let normalY = lateralZ * tx - lateralX * tz;
      let normalZ = lateralX * ty;
      let normalLength = Math.hypot(normalX, normalY, normalZ);
      if (normalLength < 1e-9) {
        normalX = 0;
        normalY = 1;
        normalZ = 0;
        normalLength = 1;
      }
      normalX /= normalLength;
      normalY /= normalLength;
      normalZ /= normalLength;

      const cx = points[i * 3];
      const cy = points[i * 3 + 1];
      const cz = points[i * 3 + 2];

      positions[vertexWrite] = cx + lateralX * halfWidth;
      positions[vertexWrite + 1] = cy;
      positions[vertexWrite + 2] = cz + lateralZ * halfWidth;
      normals[vertexWrite] = normalX;
      normals[vertexWrite + 1] = normalY;
      normals[vertexWrite + 2] = normalZ;
      vertexWrite += 3;

      positions[vertexWrite] = cx - lateralX * halfWidth;
      positions[vertexWrite + 1] = cy;
      positions[vertexWrite + 2] = cz - lateralZ * halfWidth;
      normals[vertexWrite] = normalX;
      normals[vertexWrite + 1] = normalY;
      normals[vertexWrite + 2] = normalZ;
      vertexWrite += 3;
    }

    for (let i = 0; i < pointCount - 1; i += 1) {
      const left = vertexStart + i * 2;
      const right = left + 1;
      const nextLeft = left + 2;
      const nextRight = left + 3;
      indices[indexWrite] = left;
      indices[indexWrite + 1] = nextLeft;
      indices[indexWrite + 2] = right;
      indices[indexWrite + 3] = right;
      indices[indexWrite + 4] = nextLeft;
      indices[indexWrite + 5] = nextRight;
      indexWrite += 6;
    }
  }

  return { positions, normals, indices };
}

function collectRouteEdges(world: World, routeId: number): Edge[] {
  const route = world.getRoute(routeId);
  if (!route) {
    throw new WorldFormatError(`cannot build road geometry: route ${routeId} does not exist`);
  }
  return route.edgeIds.map((edgeId) => {
    const edge = world.getEdge(edgeId);
    if (!edge) {
      throw new WorldFormatError(`route ${routeId} references missing edge ${edgeId}`);
    }
    return edge;
  });
}

function collectGraphEdges(world: World): Edge[] {
  const edges: Edge[] = [];
  for (let id = 0; ; id += 1) {
    const edge = world.getEdge(id);
    if (!edge) break;
    edges.push(edge);
  }
  return edges;
}

function validateEdgeForGeometry(edge: Edge): number {
  const pointCount = edge.points.length / 3;
  if (!Number.isInteger(pointCount) || pointCount < 2) {
    throw new WorldFormatError(`edge ${edge.id} needs at least two x/y/z points`);
  }
  if (!Number.isFinite(edge.width) || edge.width <= 0) {
    throw new WorldFormatError(`edge ${edge.id} has invalid width`);
  }
  return pointCount;
}

function positiveAscent(points: Float32Array): number {
  let ascent = 0;
  for (let i = 3; i < points.length; i += 3) {
    const rise = points[i + 1] - points[i - 2];
    if (rise > 0) ascent += rise;
  }
  return ascent;
}

function findProfileIndex(distances: Float64Array, s: number): number {
  let low = 0;
  let high = distances.length - 2;
  while (low < high) {
    const middle = (low + high + 1) >> 1;
    if (distances[middle] <= s) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return low;
}

function readFinite(reader: BinaryReader, label: string): number {
  const value = reader.f32(label);
  if (!Number.isFinite(value)) {
    throw new WorldFormatError(`${label} is not finite`);
  }
  return value;
}

function parseManifestMajor(version: unknown): number | null {
  if (typeof version !== "string") return null;
  const match = /^(\d+)(?:\.|$)/.exec(version);
  if (!match) return null;
  const major = Number(match[1]);
  return Number.isInteger(major) ? major : null;
}

function isVec3(value: unknown): value is Vec3 {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<Vec3>;
  return (
    Number.isFinite(candidate.x) &&
    Number.isFinite(candidate.y) &&
    Number.isFinite(candidate.z)
  );
}

function isSegmentType(value: string): value is SegmentType {
  return (SEGMENT_TYPES as readonly string[]).includes(value);
}

function sameNumbers(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new WorldFormatError("SHA-256 verification requires Web Crypto (crypto.subtle)");
  }
  const digest = new Uint8Array(await subtle.digest("SHA-256", buffer));
  let hex = "";
  for (const byte of digest) {
    hex += byte.toString(16).padStart(2, "0");
  }
  return hex;
}
